import React, { useEffect, useMemo, useState, useRef } from 'react';
import { getAuthHeader, getUserInfo } from '../utils/tokenManager';
import { getManualCodes, createSupplierPartner, saveSharedInventory, createSharedSeries, deleteSharedSeries, getSeriesManagement, registerSeriesInventory, getSeriesDateInfo, updateSeriesDateInfo } from '../services/apiService';

const PROC_NAME = 'DBO.TRP_S_%EC%8B%9C%EB%A6%AC%EC%A6%88%EB%A6%AC%EC%8A%A4%ED%8A%B8_%EC%8B%9C%EB%A6%AC%EC%A6%88%EC%A2%85%EB%A5%98';

const SharedInventoryManagement = () => {
  const [seriesType, setSeriesType] = useState('347001'); // 347001: 항공, 347002: 호텔
  const [seriesName, setSeriesName] = useState('');
  const [rows, setRows] = useState([]);
  const [colFilters, setColFilters] = useState({});
  const [sortKey, setSortKey] = useState('');
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSharedSeatOnly, setHasSharedSeatOnly] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newSeriesType, setNewSeriesType] = useState('347001');
  const [newRow, setNewRow] = useState({});
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [supplierFilter, setSupplierFilter] = useState('');
  const [isSupplierOpen, setIsSupplierOpen] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState('');
  const [creatingSupplier, setCreatingSupplier] = useState(false);
  const [airportOptions, setAirportOptions] = useState([]);
  const [departAirportOpen, setDepartAirportOpen] = useState(false);
  const [arriveAirportOpen, setArriveAirportOpen] = useState(false);
  const [departAirportFilter, setDepartAirportFilter] = useState('');
  const [arriveAirportFilter, setArriveAirportFilter] = useState('');
  const [isSeatModalOpen, setIsSeatModalOpen] = useState(false);
  const [seatData, setSeatData] = useState([]);
  const [loadingSeatData, setLoadingSeatData] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    startDate: '',
    endDate: '',
    seatCount: '',
    weekdays: [1, 2, 3, 4, 5, 6, 7], // 1=월, 2=화, ..., 7=일
  });
  const [isRegistering, setIsRegistering] = useState(false);
  const [isSeatEditModalOpen, setIsSeatEditModalOpen] = useState(false);
  const [seatEditDetail, setSeatEditDetail] = useState(null);
  const [seatEditSeatCount, setSeatEditSeatCount] = useState('');
  const [seatEditItemNo, setSeatEditItemNo] = useState(null);
  const [isSeatDetailLoading, setIsSeatDetailLoading] = useState(false);
  const [isSeatSaving, setIsSeatSaving] = useState(false);
  // 시리즈 추가 시 좌석 등록 폼
  const [newSeriesRegisterForm, setNewSeriesRegisterForm] = useState({
    startDate: '',
    endDate: '',
    seatCount: '',
    weekdays: [1, 2, 3, 4, 5, 6, 7],
  });
  const [enableSeatRegistration, setEnableSeatRegistration] = useState(false);

  // Refs for click-outside detection (공항/공급거래처 드롭다운)
  const departCreateRef = useRef(null);
  const departEditRef = useRef(null);
  const arriveCreateRef = useRef(null);
  const arriveEditRef = useRef(null);
  const supplierCreateRef = useRef(null);
  const supplierEditRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      const createEl = departCreateRef.current;
      const editEl = departEditRef.current;
      const clickedInsideCreate = createEl && createEl.contains(e.target);
      const clickedInsideEdit = editEl && editEl.contains(e.target);
      if (departAirportOpen && !clickedInsideCreate && !clickedInsideEdit) {
        setDepartAirportOpen(false);
      }

      const aCreateEl = arriveCreateRef.current;
      const aEditEl = arriveEditRef.current;
      const clickedInsideArriveCreate = aCreateEl && aCreateEl.contains(e.target);
      const clickedInsideArriveEdit = aEditEl && aEditEl.contains(e.target);
      if (arriveAirportOpen && !clickedInsideArriveCreate && !clickedInsideArriveEdit) {
        setArriveAirportOpen(false);
      }

      const sCreateEl = supplierCreateRef.current;
      const sEditEl = supplierEditRef.current;
      const clickedInsideSupplierCreate = sCreateEl && sCreateEl.contains(e.target);
      const clickedInsideSupplierEdit = sEditEl && sEditEl.contains(e.target);
      if (isSupplierOpen && !clickedInsideSupplierCreate && !clickedInsideSupplierEdit) {
        setIsSupplierOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [departAirportOpen, arriveAirportOpen, isSupplierOpen]);

  const { companyNo, userNo } = getUserInfo() || {};

  const CONTRACT_CODE_TO_LABEL = useMemo(() => ({
    '351001': 'ADM',
    '351100': 'Hard Block',
    '351101': 'Soft Block',
  }), []);

  const CONTRACT_LABEL_TO_CODE = useMemo(() => ({
    'ADM': '351001',
    'Hard Block': '351100',
    'Soft Block': '351101',
  }), []);

  const SERIES_TYPE_LABEL = useMemo(() => ({
    '347001': '항공',
    '347002': '호텔',
    '347003': '선박',
  }), []);

  const normalizeContractCode = (val) => {
    if (val == null) return '';
    const s = String(val);
    if (CONTRACT_CODE_TO_LABEL[s]) return s;
    return CONTRACT_LABEL_TO_CODE[s] || s;
  };

  const fetchList = async () => {
    try {
      setLoading(true);
      setError('');
      const payload = {
        Table1: [
          { 컬럼명: '@공급업체번호', 값: String(companyNo ?? '1') },
          { 컬럼명: '@유저번호', 값: String(userNo ?? '') },
          { 컬럼명: '@시리즈명', 값: String(seriesName ?? '') },
          { 컬럼명: '@시리즈종류', 값: String(seriesType) },
        ],
      };
      const url = `https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=${PROC_NAME}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('load failed');
      const data = await res.json();
      setRows(Array.isArray(data?.table) ? data.table : []);
    } catch (e) {
      setError('데이터를 불러오지 못했습니다.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [seriesType]);

  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        const list = await getManualCodes({ kind: 'CW지급수배업체' });
        setSupplierOptions(Array.isArray(list) ? list : []);
      } catch (_) {
        setSupplierOptions([]);
      }
    };
    const loadAirports = async () => {
      try {
        const list = await getManualCodes({ kind: '공항' });
        setAirportOptions(Array.isArray(list) ? list : []);
      } catch (_) {
        setAirportOptions([]);
      }
    };
    if (isEditorOpen || isCreateOpen) {
      loadSuppliers();
      loadAirports();
    }
  }, [isEditorOpen, isCreateOpen]);

  const filteredSuppliers = useMemo(() => {
    const q = String(supplierFilter || '').trim();
    if (!q) return supplierOptions;
    return supplierOptions.filter((r) => {
      const code = String(r?.코드 ?? '');
      const name = String(r?.코드명 ?? '');
      return code.includes(q) || name.includes(q);
    });
  }, [supplierFilter, supplierOptions]);

  const filteredDepartAirports = useMemo(() => {
    const q = String(departAirportFilter || '').trim();
    if (!q) return airportOptions;
    const qLower = q.toLowerCase();
    return airportOptions.filter((r) => {
      const name = String(r?.코드명 || '');
      const code = String(r?.코드 || '');
      return (
        name.toLowerCase().includes(qLower) ||
        code.toLowerCase().includes(qLower)
      );
    });
  }, [departAirportFilter, airportOptions]);

  const filteredArriveAirports = useMemo(() => {
    const q = String(arriveAirportFilter || '').trim();
    if (!q) return airportOptions;
    const qLower = q.toLowerCase();
    return airportOptions.filter((r) => {
      const name = String(r?.코드명 || '');
      const code = String(r?.코드 || '');
      return (
        name.toLowerCase().includes(qLower) ||
        code.toLowerCase().includes(qLower)
      );
    });
  }, [arriveAirportFilter, airportOptions]);

  const normalizeDateString = (val) => {
    if (!val) return '';
    const s = String(val);
    // strip trailing " (요일)" if present
    return s.includes(' ') ? s.split(' ')[0] : s;
  };

  const openSeatStatus = async (row) => {
    const seriesNo = Number(row?.['시리즈번호'] ?? 0);
    if (!seriesNo) {
      alert('시리즈번호가 없습니다.');
      return;
    }
    try {
      setLoadingSeatData(true);
      setSelectedRow(row);
      const today = new Date();
      const oneYearLater = new Date(today);
      oneYearLater.setFullYear(today.getFullYear() + 1);
      const startDate = today.toISOString().split('T')[0];
      const endDate = oneYearLater.toISOString().split('T')[0];
      const data = await getSeriesManagement({
        startDate,
        endDate,
        searchCriteria: '출발일',
        seriesNo,
      });
      setSeatData(Array.isArray(data) ? data : []);
      setIsSeatModalOpen(true);
    } catch (e) {
      alert('좌석 정보를 불러오지 못했습니다.');
      setSeatData([]);
    } finally {
      setLoadingSeatData(false);
    }
  };

  const openSeatEdit = async (seatRow) => {
    const sharedSeriesItemNo =
      seatRow?.['상품공유일련번호'] ??
      seatRow?.['상품공유번호'] ??
      seatRow?.['상품공유일자번호'] ??
      seatRow?.['일련번호'] ??
      seatRow?.['공유일련번호'] ??
      null;
    if (!sharedSeriesItemNo) {
      alert('좌석 상세 식별자가 없습니다.');
      return;
    }
    try {
      setIsSeatDetailLoading(true);
      setSeatEditItemNo(sharedSeriesItemNo);
      const { table } = await getSeriesDateInfo({ sharedSeriesItemNo });
      const detail = Array.isArray(table) ? table[0] : null;
      if (!detail) {
        alert('좌석 상세 정보를 불러오지 못했습니다.');
        return;
      }
      setSeatEditDetail(detail);
      setSeatEditSeatCount(String(detail?.['좌석수'] ?? ''));
      setIsSeatEditModalOpen(true);
    } catch (e) {
      alert('좌석 상세 정보를 불러오지 못했습니다.');
    } finally {
      setIsSeatDetailLoading(false);
    }
  };

  const handleSaveSeatEdit = async () => {
    if (!seatEditDetail) return;
    const sharedSeriesItemNo =
      seatEditDetail?.['상품공유일련번호'] ??
      seatEditDetail?.['상품공유번호'] ??
      seatEditDetail?.['상품공유일자번호'] ??
      seatEditDetail?.['일련번호'] ??
      seatEditDetail?.['공유일련번호'] ??
      seatEditItemNo ??
      null;
    if (!sharedSeriesItemNo) {
      alert('좌석 상세 식별자가 없습니다.');
      return;
    }
    const seatCountNum = Number(seatEditSeatCount);
    if (Number.isNaN(seatCountNum) || seatCountNum < 0) {
      alert('좌석수를 올바르게 입력해주세요.');
      return;
    }
    try {
      setIsSeatSaving(true);
      await updateSeriesDateInfo({
        companyNo,
        userNo,
        sharedSeriesItemNo,
        seatCount: seatCountNum,
        seatStatus: seatEditDetail?.['좌석상태코드'] ?? seatEditDetail?.['좌석상태'] ?? '',
        nameTl: normalizeDateString(seatEditDetail?.['네임티엘'] ?? ''),
        ticketTl: normalizeDateString(seatEditDetail?.['발권티엘'] ?? ''),
        flightCurrencyAdult: seatEditDetail?.['항공료성인'] ?? 0,
        flightCurrencyChild: seatEditDetail?.['항공료소아'] ?? 0,
        flightCurrencyInfant: seatEditDetail?.['항공료유아'] ?? 0,
        fuelCurrencyAdult: seatEditDetail?.['택스및유류성인'] ?? 0,
        fuelCurrencyChild: seatEditDetail?.['택스및유류소아'] ?? 0,
        fuelCurrencyInfant: seatEditDetail?.['택스및유류유아'] ?? 0,
        reserveClose: seatEditDetail?.['예약마감'] ?? false,
        ticketClose: seatEditDetail?.['발권마감'] ?? false,
        flightJson: seatEditDetail?.['항공여정_JSON'] ?? '[]',
        cutoffJson: seatEditDetail?.['컷오프_JSON'] ?? '[]',
        addPnrJson: seatEditDetail?.['추가PNR_JSON'] ?? '[]',
        pnrType: seatEditDetail?.['피엔알구분'] ?? '',
        pnrInfo: seatEditDetail?.['피엔알정보'] ?? '',
        flightClass: seatEditDetail?.['클래스'] ?? null,
        pnrNo: seatEditDetail?.['피엔알번호'] ?? seatEditDetail?.['PNR번호'] ?? '',
        memo: seatEditDetail?.['비고'] ?? '',
        pnrNameCount: seatEditDetail?.['피엔알네임갯수'] ?? seatEditDetail?.['PNR네임갯수'] ?? 0,
        ticketOsNo: seatEditDetail?.['발권오스번호'] ?? '',
      });
      alert('좌석 정보가 저장되었습니다.');
      setIsSeatEditModalOpen(false);
      setSeatEditDetail(null);
      // refresh seat status list
      const seriesNo = Number(selectedRow?.['시리즈번호'] ?? 0);
      if (seriesNo) {
        const today = new Date();
        const oneYearLater = new Date(today);
        oneYearLater.setFullYear(today.getFullYear() + 1);
        const startDate = today.toISOString().split('T')[0];
        const endDate = oneYearLater.toISOString().split('T')[0];
        const data = await getSeriesManagement({
          startDate,
          endDate,
          searchCriteria: '출발일',
          seriesNo,
        });
        setSeatData(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      alert('좌석 정보 저장에 실패했습니다.');
    } finally {
      setIsSeatSaving(false);
    }
  };

  const handleCreateSupplier = async () => {
    const name = (newSupplierName || '').trim();
    if (!name) return;
    try {
      setCreatingSupplier(true);
      await createSupplierPartner({ name });
      const list = await getManualCodes({ kind: 'CW지급수배업체' });
      setSupplierOptions(Array.isArray(list) ? list : []);
      const found = (Array.isArray(list) ? list : []).find((r) => String(r?.코드명 || '') === name);
      if (found) {
        const v = String(found.코드 ?? '');
        setSelectedRow((prev) => ({
          ...prev,
          ['공급거래처번호']: v,
          ['공급거래처']: String(found.코드명 ?? ''),
        }));
      }
      setNewSupplierName('');
      setIsSupplierOpen(false);
    } catch (e) {
      alert('거래처 생성에 실패했습니다.');
    } finally {
      setCreatingSupplier(false);
    }
  };

  const columns = useMemo(() => {
    if (seriesType !== '347002') {
      // 항공
      return [
        { key: '시리즈번호', label: '시리즈번호' },
        { key: '시리즈명', label: '시리즈명' },
        { key: '공급거래처', label: '공급거래처' },
        { key: '출발편명', label: '출발편명' },
        { key: '귀국편명', label: '귀국편명' },
        { key: '출발공항', label: '출발지' },
        { key: '목적공항', label: '도착지' },
        { key: '박수', label: '박수' },
        { key: '일수', label: '일수' },
        { key: '시리즈시작일', label: '시리즈시작일' },
        { key: '시리즈종료일', label: '시리즈종료일' },
        { key: '계약종류', label: '계약종류' },
      ];
    }
    // 호텔
    return [
      { key: '시리즈번호', label: '시리즈번호' },
      { key: '시리즈명', label: '시리즈명' },
      { key: '공급거래처', label: '공급거래처' },
      { key: '호텔명', label: '호텔명' },
      { key: '객실타입', label: '객실타입' },
      { key: '시리즈시작일', label: '시리즈시작일' },
      { key: '시리즈종료일', label: '시리즈종료일' },
    ];
  }, [seriesType]);

  const filteredRows = useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const activeFilters = colFilters || {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return rows.filter((row) => {
      if (hasSharedSeatOnly) {
        const endStr = String(row?.['시리즈종료일'] ?? '').trim();
        const endDate = endStr ? new Date(endStr) : null;
        if (!endDate || isNaN(endDate.getTime()) || !(endDate > today)) return false;
      }
      return (columns || []).every((col) => {
        const f = String(activeFilters[col.key] ?? '').trim().toLowerCase();
        if (!f) return true;
        const v = String(row?.[col.key] ?? '').toLowerCase();
        return v.includes(f);
      });
    });
  }, [rows, colFilters, columns, hasSharedSeatOnly]);

  useEffect(() => {
    setColFilters({});
  }, [seriesType]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    if (!sortKey) return arr;
    const dir = sortDir === 'desc' ? -1 : 1;
    return arr.sort((a, b) => {
      const av = a?.[sortKey];
      const bv = b?.[sortKey];
      const aNum = Number(av);
      const bNum = Number(bv);
      const aIsNum = !isNaN(aNum) && String(av).trim() !== '';
      const bIsNum = !isNaN(bNum) && String(bv).trim() !== '';
      if (aIsNum && bIsNum) return (aNum - bNum) * dir;
      return String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true }) * dir;
    });
  }, [filteredRows, sortKey, sortDir]);

  useEffect(() => {
    setSortKey('');
    setSortDir('asc');
  }, [seriesType]);

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-white p-5">
        <div className="flex flex-col md:flex-row md:items-end md:space-x-4 space-y-3 md:space-y-0">
          <div>
            <label className="block text-[13px] font-semibold text-gray-600 mb-1">시리즈 종류</label>
            <select
              className="h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
              value={seriesType}
              onChange={(e) => setSeriesType(e.target.value)}
            >
              <option value="347001">항공</option>
              <option value="347002">호텔</option>
              <option value="347003">선박</option>
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-[13px] font-semibold text-gray-600 mb-1">시리즈명</label>
            <input
              type="text"
              value={seriesName}
              onChange={(e) => setSeriesName(e.target.value)}
              placeholder="시리즈명을 입력하세요"
              className="h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none w-full"
              onKeyDown={(e) => { if (e.key === 'Enter') fetchList(); }}
            />
          </div>
          <div>
            <label className="block text-[13px] font-semibold text-gray-600 mb-1"> </label>
            <label className="inline-flex items-center text-sm text-gray-700 select-none">
              <input
                type="checkbox"
                className="mr-2"
                checked={hasSharedSeatOnly}
                onChange={(e) => setHasSharedSeatOnly(e.target.checked)}
              />
              공유좌석 있음
            </label>
          </div>
          <div>
            <button
              onClick={fetchList}
              className="h-9 rounded-lg px-3.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700"
            >
              검색
            </button>
          </div>
          <div>
           
          </div>
          <div>
            <button
              onClick={() => { 
                setNewSeriesType(seriesType); 
                setNewRow({ 시리즈명: '', 계약종류: '', 호텔명: '', 객실타입: '' }); 
                setNewSeriesRegisterForm({ startDate: '', endDate: '', seatCount: '', weekdays: [1, 2, 3, 4, 5, 6, 7] });
                setEnableSeatRegistration(false);
                setIsCreateOpen(true); 
              }}
              className="h-9 rounded-lg px-3.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700"
            >
              시리즈 추가
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-white p-5 overflow-hidden">
        {loading && (
          <div className="py-16 text-center text-sm text-gray-500">불러오는 중...</div>
        )}

      {isCreateOpen && (
        <div className="fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={() => setIsCreateOpen(false)} />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl bg-white shadow-2xl rounded-2xl z-50 flex flex-col max-h-[90vh]">
            <div className="px-6 pt-5 pb-3 flex items-center justify-between">
              <h2 className="text-[17px] font-bold text-gray-900">시리즈 추가</h2>
              <button className="h-8 w-8 rounded-lg text-gray-400 hover:bg-gray-100" onClick={() => setIsCreateOpen(false)} aria-label="Close">✕</button>
            </div>
            <div className="px-6 py-4 overflow-auto flex-1 space-y-4">
              <div
                className={`mb-4 rounded-md border px-3 py-2 text-xs font-medium flex items-center gap-2 ${
                  newSeriesType === '347001'
                    ? 'bg-sky-50 border-sky-100 text-sky-800'
                    : newSeriesType === '347002'
                    ? 'bg-amber-50 border-amber-100 text-amber-800'
                    : 'bg-emerald-50 border-emerald-100 text-emerald-800'
                }`}
              >
                <span
                  className={`inline-block h-2.5 w-2.5 rounded-full ${
                    newSeriesType === '347001'
                      ? 'bg-sky-500'
                      : newSeriesType === '347002'
                      ? 'bg-amber-500'
                      : 'bg-emerald-500'
                  }`}
                />
                <span className="text-xs">
                  {SERIES_TYPE_LABEL[String(newSeriesType)] || '시리즈'} 시리즈를 추가합니다.
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3 items-center">
                <label className="text-[13px] font-semibold text-gray-600">시리즈 종류</label>
                <select
                  className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                  value={newSeriesType}
                  onChange={(e) => setNewSeriesType(e.target.value)}
                >
                  <option value="347001">항공</option>
                  <option value="347002">호텔</option>
                  <option value="347003">선박</option>
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3 items-center">
                <label className="text-[13px] font-semibold text-gray-600">시리즈명</label>
                <input
                  className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                  value={String(newRow?.['시리즈명'] ?? '')}
                  onChange={(e) => setNewRow(prev => ({ ...prev, ['시리즈명']: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-3 gap-3 items-center">
                <label className="text-[13px] font-semibold text-gray-600">계약종류</label>
                <select
                  className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                  value={normalizeContractCode(newRow?.['계약종류'])}
                  onChange={(e) => setNewRow(prev => ({ ...prev, ['계약종류']: e.target.value }))}
                >
                  <option value="">선택</option>
                  <option value="351001">ADM</option>
                  <option value="351100">Hard Block</option>
                  <option value="351101">Soft Block</option>
                </select>
              </div>

              {newSeriesType !== '347002' ? (
                <>
                  <div className="grid grid-cols-3 gap-3 items-center">
                    <label className="text-[13px] font-semibold text-gray-600">박수</label>
                    <input
                      type="number"
                      className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                      value={String(newRow?.['박수'] ?? '')}
                      onChange={(e) => setNewRow(prev => ({ ...prev, ['박수']: e.target.value }))}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-3 items-center">
                    <label className="text-[13px] font-semibold text-gray-600">일수</label>
                    <input
                      type="number"
                      className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                      value={String(newRow?.['일수'] ?? '')}
                      onChange={(e) => setNewRow(prev => ({ ...prev, ['일수']: e.target.value }))}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-3 items-center">
                    <label className="text-[13px] font-semibold text-gray-600">가는편</label>
                    <input
                      className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                      value={String(newRow?.['출발편명'] ?? '')}
                      onChange={(e) => setNewRow(prev => ({ ...prev, ['출발편명']: e.target.value }))}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-3 items-center">
                    <label className="text-[13px] font-semibold text-gray-600">오는편</label>
                    <input
                      className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                      value={String(newRow?.['귀국편명'] ?? '')}
                      onChange={(e) => setNewRow(prev => ({ ...prev, ['귀국편명']: e.target.value }))}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-3 items-center">
                    <label className="text-[13px] font-semibold text-gray-600">출발지</label>
                    <div className="col-span-2 relative" ref={departCreateRef}>
                      <button
                        type="button"
                        className="w-full border border-gray-300 rounded-lg h-9 px-2.5 text-sm text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        onClick={() => setDepartAirportOpen((v) => !v)}
                      >
                        {String(newRow?.['출발공항명'] ?? '') || String(newRow?.['출발공항'] ?? '') || '선택하세요'}
                      </button>
                      {departAirportOpen && (
                        <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow">
                          <div className="p-2 border-b">
                            <input
                              autoFocus
                              type="text"
                              className="w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
                              placeholder="검색 (코드/명)"
                              value={departAirportFilter}
                              onChange={(e) => setDepartAirportFilter(e.target.value)}
                            />
                          </div>
                          <div className="max-h-56 overflow-auto">
                            {filteredDepartAirports.length === 0 && (
                              <div className="px-3 py-2 text-sm text-gray-500">검색 결과가 없습니다</div>
                            )}
                            {filteredDepartAirports.map((r) => {
                              const code = String(r?.코드 ?? '');
                              const label = String(r?.코드명 ?? '');
                              return (
                                <button
                                  key={`${code}-${label}`}
                                  type="button"
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${String(newRow?.['출발공항'] ?? '') === code ? 'bg-blue-50' : ''}`}
                                  onClick={() => {
                                    setNewRow((prev) => ({ ...prev, ['출발공항']: code, ['출발공항명']: label }));
                                    setDepartAirportOpen(false);
                                  }}
                                >
                                  {label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 items-center">
                    <label className="text-[13px] font-semibold text-gray-600">목적지</label>
                    <div className="col-span-2 relative" ref={arriveCreateRef}>
                      <button
                        type="button"
                        className="w-full border border-gray-300 rounded-lg h-9 px-2.5 text-sm text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        onClick={() => setArriveAirportOpen((v) => !v)}
                      >
                        {String(newRow?.['목적공항명'] ?? '') || String(newRow?.['목적공항'] ?? '') || '선택하세요'}
                      </button>
                      {arriveAirportOpen && (
                        <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow">
                          <div className="p-2 border-b">
                            <input
                              autoFocus
                              type="text"
                              className="w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
                              placeholder="검색 (코드/명)"
                              value={arriveAirportFilter}
                              onChange={(e) => setArriveAirportFilter(e.target.value)}
                            />
                          </div>
                          <div className="max-h-56 overflow-auto">
                            {filteredArriveAirports.length === 0 && (
                              <div className="px-3 py-2 text-sm text-gray-500">검색 결과가 없습니다</div>
                            )}
                            {filteredArriveAirports.map((r) => {
                              const code = String(r?.코드 ?? '');
                              const label = String(r?.코드명 ?? '');
                              return (
                                <button
                                  key={`${code}-${label}`}
                                  type="button"
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${String(newRow?.['목적공항'] ?? '') === code ? 'bg-blue-50' : ''}`}
                                  onClick={() => {
                                    setNewRow((prev) => ({ ...prev, ['목적공항']: code, ['목적공항명']: label }));
                                    setArriveAirportOpen(false);
                                  }}
                                >
                                  {label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-3 items-center">
                    <label className="text-[13px] font-semibold text-gray-600">호텔명</label>
                    <input
                      className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                      value={String(newRow?.['호텔명'] ?? '')}
                      onChange={(e) => setNewRow(prev => ({ ...prev, ['호텔명']: e.target.value }))}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3 items-center">
                    <label className="text-[13px] font-semibold text-gray-600">객실타입</label>
                    <input
                      className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                      value={String(newRow?.['객실타입'] ?? '')}
                      onChange={(e) => setNewRow(prev => ({ ...prev, ['객실타입']: e.target.value }))}
                    />
                  </div>
                </>
              )}

              <div className="grid grid-cols-3 gap-3 items-center">
                <label className="text-[13px] font-semibold text-gray-600">공급거래처</label>
                <div className="col-span-2">
                  <div className="relative" ref={supplierCreateRef}>
                    <button
                      type="button"
                      className="w-full border border-gray-300 rounded-lg h-9 px-2.5 text-sm text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                      onClick={() => setIsSupplierOpen((v) => !v)}
                    >
                      {String(newRow?.['공급거래처'] ?? '') || '선택하세요'}
                    </button>
                    {isSupplierOpen && (
                      <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow">
                        <div className="p-2 border-b">
                          <input
                            autoFocus
                            type="text"
                            className="w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
                            placeholder="검색 (코드/이름)"
                            value={supplierFilter}
                            onChange={(e) => setSupplierFilter(e.target.value)}
                          />
                        </div>
                        <div className="max-h-56 overflow-auto">
                          {filteredSuppliers.length === 0 && (
                            <div className="px-3 py-2 text-sm text-gray-500">검색 결과가 없습니다</div>
                          )}
                          {filteredSuppliers.map((r) => {
                            const v = String(r?.코드 ?? '');
                            const label = String(r?.코드명 ?? '');
                            return (
                              <button
                                key={v}
                                type="button"
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${String(newRow?.['공급거래처번호'] ?? '') === v ? 'bg-blue-50' : ''}`}
                                onClick={() => {
                                  setNewRow((prev) => ({
                                    ...prev,
                                    ['공급거래처번호']: v,
                                    ['공급거래처']: label,
                                  }));
                                  setIsSupplierOpen(false);
                                }}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 items-center">
               
              </div>

              {/* 좌석 등록 섹션 */}
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    id="enableSeatRegistration"
                    checked={enableSeatRegistration}
                    onChange={(e) => setEnableSeatRegistration(e.target.checked)}
                    className="rounded"
                  />
                  <label htmlFor="enableSeatRegistration" className="text-sm font-medium text-gray-700 cursor-pointer">
                    시리즈 추가 시 좌석도 함께 등록
                  </label>
                </div>

                {enableSeatRegistration && (
                  <div className="bg-blue-50 rounded-xl p-4 space-y-3">
                    <div className="text-xs text-blue-700 mb-2">
                      시리즈 생성과 동시에 출발일별 좌석을 등록합니다.
                    </div>
                    <div className="grid grid-cols-3 gap-3 items-center">
                      <label className="text-[13px] font-semibold text-gray-600">시작일</label>
                      <input
                        type="date"
                        className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        value={newSeriesRegisterForm.startDate}
                        onChange={(e) => setNewSeriesRegisterForm(prev => ({ ...prev, startDate: e.target.value }))}
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-3 items-center">
                      <label className="text-[13px] font-semibold text-gray-600">종료일</label>
                      <input
                        type="date"
                        className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        value={newSeriesRegisterForm.endDate}
                        onChange={(e) => setNewSeriesRegisterForm(prev => ({ ...prev, endDate: e.target.value }))}
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-3 items-center">
                      <label className="text-[13px] font-semibold text-gray-600">좌석수</label>
                      <input
                        type="number"
                        min="0"
                        className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        value={newSeriesRegisterForm.seatCount}
                        onChange={(e) => setNewSeriesRegisterForm(prev => ({ ...prev, seatCount: e.target.value }))}
                        placeholder="좌석수 입력"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-3 items-start">
                      <label className="text-[13px] font-semibold text-gray-600 pt-2">적용요일</label>
                      <div className="col-span-2 space-y-2">
                        <div className="flex flex-wrap gap-2">
                          {[
                            { value: 1, label: '월' },
                            { value: 2, label: '화' },
                            { value: 3, label: '수' },
                            { value: 4, label: '목' },
                            { value: 5, label: '금' },
                            { value: 6, label: '토' },
                            { value: 7, label: '일' },
                          ].map(({ value, label }) => (
                            <label key={value} className="flex items-center gap-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={newSeriesRegisterForm.weekdays.includes(value)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setNewSeriesRegisterForm(prev => ({
                                      ...prev,
                                      weekdays: [...prev.weekdays, value].sort((a, b) => a - b),
                                    }));
                                  } else {
                                    setNewSeriesRegisterForm(prev => ({
                                      ...prev,
                                      weekdays: prev.weekdays.filter(w => w !== value),
                                    }));
                                  }
                                }}
                                className="rounded"
                              />
                              <span className="text-sm text-gray-700">{label}</span>
                            </label>
                          ))}
                        </div>
                        <button
                          type="button"
                          className="text-xs text-blue-600 hover:text-blue-700"
                          onClick={() => {
                            const allSelected = newSeriesRegisterForm.weekdays.length === 7;
                            setNewSeriesRegisterForm(prev => ({
                              ...prev,
                              weekdays: allSelected ? [] : [1, 2, 3, 4, 5, 6, 7],
                            }));
                          }}
                        >
                          {newSeriesRegisterForm.weekdays.length === 7 ? '전체 해제' : '전체 선택'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="px-6 pb-5 pt-3 flex justify-end space-x-2">
              <button className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white hover:bg-gray-50" onClick={() => setIsCreateOpen(false)}>닫기</button>
              <button
                className="h-9 rounded-lg px-3.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isRegistering}
                onClick={async () => {
                  // 좌석 등록 옵션이 켜져 있으면 유효성 검사
                  if (enableSeatRegistration) {
                    if (!newSeriesRegisterForm.startDate || !newSeriesRegisterForm.endDate) {
                      alert('좌석 등록을 위해 시작일과 종료일을 입력해주세요.');
                      return;
                    }
                    if (!newSeriesRegisterForm.seatCount || Number(newSeriesRegisterForm.seatCount) <= 0) {
                      alert('좌석 등록을 위해 좌석수를 입력해주세요.');
                      return;
                    }
                    if (newSeriesRegisterForm.weekdays.length === 0) {
                      alert('좌석 등록을 위해 적용요일을 선택해주세요.');
                      return;
                    }
                  }

                  try {
                    setIsRegistering(true);
                    const params = {
                      supplierPartnerNo: newRow?.['공급거래처번호'] != null ? String(newRow['공급거래처번호']) : '',
                      seriesName: String(newRow?.['시리즈명'] ?? ''),
                      stockType: String(newSeriesType),
                      contractType: String(normalizeContractCode(newRow?.['계약종류'] ?? '')),
                      departAirportCode: String(newRow?.['출발공항'] ?? ''),
                      arriveAirportCode: String(newRow?.['목적공항'] ?? ''),
                      penaltyRule: '',
                      color: String(newRow?.['색상'] ?? '#000000'),
                      departFlightNo: String(newRow?.['출발편명'] ?? ''),
                      returnFlightNo: String(newRow?.['귀국편명'] ?? ''),
                      nights: newRow?.['박수'] != null ? String(newRow['박수']) : '',
                      days: newRow?.['일수'] != null ? String(newRow['일수']) : '',
                      oneWayType: '왕복',
                      airlineExtras: '',
                      airlineExtrasName: '',
                      hotelName: newSeriesType === '347002' ? String(newRow?.['호텔명'] ?? '') : '',
                      roomType: newSeriesType === '347002' ? String(newRow?.['객실타입'] ?? '') : '',
                    };
                    const result = await createSharedSeries(params);
                    
                    // 좌석 등록 옵션이 켜져 있으면 좌석도 등록
                    if (enableSeatRegistration) {
                      // 생성된 시리즈 번호 추출 (응답에서 시리즈번호 찾기)
                      let newSeriesNo = null;
                      if (result?.table && Array.isArray(result.table) && result.table.length > 0) {
                        newSeriesNo = result.table[0]?.['시리즈번호'] || result.table[0]?.['상품공유번호'];
                      }
                      
                      if (!newSeriesNo) {
                        // 시리즈 번호를 못 찾으면 리스트를 다시 조회해서 방금 생성된 시리즈 찾기
                        await fetchList();
                        alert('시리즈가 추가되었습니다. 좌석 등록은 시리즈 편집에서 진행해주세요.');
                        setIsCreateOpen(false);
                        return;
                      }

                      const weekdaysStr = newSeriesRegisterForm.weekdays.sort((a, b) => a - b).join(',');
                      await registerSeriesInventory({
                        sharedSeriesNo: newSeriesNo,
                        weekdays: weekdaysStr,
                        startDate: newSeriesRegisterForm.startDate,
                        endDate: newSeriesRegisterForm.endDate,
                        seatCount: newSeriesRegisterForm.seatCount,
                      });
                      alert('시리즈와 좌석이 함께 등록되었습니다.');
                    } else {
                      alert('시리즈가 추가되었습니다.');
                    }
                    
                    setIsCreateOpen(false);
                    fetchList();
                  } catch (e) {
                    alert('시리즈 추가에 실패했습니다.');
                  } finally {
                    setIsRegistering(false);
                  }
                }}
              >
                {isRegistering ? '처리 중...' : (enableSeatRegistration ? '시리즈 + 좌석 추가' : '추가')}
              </button>
            </div>
          </div>
        </div>
      )}
        {error && !loading && (
          <div className="py-16 text-center text-sm text-red-600">{error}</div>
        )}
        {!loading && !error && (
          <div className="overflow-auto">
            <div className="pb-3 text-sm text-gray-500 bg-white sticky top-0 z-10">
              총 {rows.length}건 · 필터 후 {sortedRows.length}건
            </div>
            <table className="min-w-full text-sm text-center">
              <thead className="bg-gray-100 sticky top-0 z-10">
                <tr>
                  {columns.map(col => (
                    <th key={col.key} className="h-10 bg-gray-100 text-center px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap select-none">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 hover:text-blue-700"
                        onClick={() => {
                          if (sortKey === col.key) {
                            if (sortDir === 'asc') {
                              setSortDir('desc');
                            } else {
                              setSortKey('');
                              setSortDir('asc');
                            }
                          } else {
                            setSortKey(col.key);
                            setSortDir('asc');
                          }
                        }}
                        title="정렬"
                      >
                        <span>{col.label}</span>
                        {sortKey === col.key && (
                          <span>{sortDir === 'asc' ? '▲' : '▼'}</span>
                        )}
                      </button>
                    </th>
                  ))}
                  <th className="h-10 bg-gray-100 text-center px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap select-none">재고현황</th>
                </tr>
                <tr>
                  {columns.map(col => (
                    <th key={col.key} className="bg-gray-100 px-3 pb-2 pt-0">
                      <input
                        type="text"
                        value={String(colFilters[col.key] ?? '')}
                        onChange={(e) => setColFilters(prev => ({ ...prev, [col.key]: e.target.value }))}
                        placeholder=""
                        className="text-center w-full h-8 rounded-lg border border-gray-300 bg-white px-2 text-xs font-light focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
                      />
                    </th>
                  ))}
                  <th className="bg-gray-100 px-3 pb-2 pt-0" />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td className="py-16 text-center text-sm text-gray-500" colSpan={columns.length + 1}>데이터가 없습니다.</td>
                  </tr>
                )}
                {rows.length > 0 && filteredRows.length === 0 && (
                  <tr>
                    <td className="py-16 text-center text-sm text-gray-500" colSpan={columns.length + 1}>필터 결과가 없습니다.</td>
                  </tr>
                )}
                {sortedRows.map((r, idx) => {
                  const endStr = String(r?.['시리즈종료일'] ?? '').trim();
                  const endDate = endStr ? new Date(endStr) : null;
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const hasSeat = endDate && !isNaN(endDate.getTime()) && endDate > today;
                  return (
                  <tr
                    key={idx}
                    className={
                      'border-b border-gray-200 hover:bg-gray-50 cursor-pointer'
                    }
                    onClick={() => {
                      setSelectedRow(r);
                      setIsEditorOpen(true);
                    }}
                  >
                    {columns.map((col, colIdx) => (
                      <td
                        key={col.key}
                        className={
                          'px-3 py-3 whitespace-nowrap ' +
                          (colIdx === columns.length - 1 ? '' : '') +
                          (hasSeat ? 'text-gray-800' : 'text-gray-400')
                        }
                      >
                        {String(r?.[col.key] ?? '')}
                      </td>
                    ))}
                    <td className="px-3 py-3 whitespace-nowrap">
                      <button
                        type="button"
                        className="h-9 rounded-lg px-3.5 text-sm font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          openSeatStatus(r);
                        }}
                        disabled={loadingSeatData}
                        title="좌석 현황 보기"
                      >
                        {loadingSeatData ? '불러오는 중...' : '재고현황'}
                      </button>
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isEditorOpen && (
        <div className="fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={() => setIsEditorOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-full sm:w-[520px] bg-white shadow-2xl z-50 flex flex-col">
            <div className="px-6 pt-5 pb-3 flex items-center justify-between">
              <h2 className="text-[17px] font-bold text-gray-900">공유재고 편집</h2>
              <button
                className="h-8 w-8 rounded-lg text-gray-400 hover:bg-gray-100"
                onClick={() => setIsEditorOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-4 overflow-auto flex-1 space-y-3">
              <div className="grid grid-cols-3 gap-3 items-center">
                <label className="text-[13px] font-semibold text-gray-600">시리즈 종류</label>
                <div className="py-3 col-span-2 text-sm text-gray-900">{SERIES_TYPE_LABEL[String(seriesType)] || String(seriesType)}</div>
              </div>
              <div className="grid grid-cols-3 gap-3 items-center">
                <div className="text-[13px] font-semibold text-gray-600">시리즈번호</div>
                <div className="py-3 col-span-2 text-sm text-gray-900">{String(selectedRow?.['시리즈번호'] ?? '')}</div>
              </div>
              <div className="grid grid-cols-3 gap-3 items-center">
                <label className="text-[13px] font-semibold text-gray-600">시리즈명</label>
                <input
                  className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                  value={String(selectedRow?.['시리즈명'] ?? '')}
                  onChange={(e) => setSelectedRow(prev => ({ ...prev, ['시리즈명']: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-3 gap-3 items-center">
                <label className="text-[13px] font-semibold text-gray-600">계약종류</label>
                <select
                  className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                  value={normalizeContractCode(selectedRow?.['계약종류'])}
                  onChange={(e) => setSelectedRow(prev => ({ ...prev, ['계약종류']: e.target.value }))}
                >
                  <option value="">선택</option>
                  <option value="351001">ADM</option>
                  <option value="351100">Hard Block</option>
                  <option value="351101">Soft Block</option>
                </select>
              </div>
              {seriesType !== '347002' ? (
                <>
                  {(selectedRow?.['박수'] != null || selectedRow?.['일수'] != null) && (
                    <div className="grid grid-cols-6 gap-3 items-center">
                      {selectedRow?.['박수'] != null ? (
                        <>
                          <label className="text-[13px] font-semibold text-gray-600">박수</label>
                          <input
                            type="number"
                            className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                            value={String(selectedRow?.['박수'] ?? '')}
                            onChange={(e) => setSelectedRow(prev => ({ ...prev, ['박수']: e.target.value }))}
                          />
                        </>
                      ) : (
                        <div className="col-span-3" />
                      )}
                      {selectedRow?.['일수'] != null ? (
                        <>
                          <label className="text-[13px] font-semibold text-gray-600">일수</label>
                          <input
                            type="number"
                            className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                            value={String(selectedRow?.['일수'] ?? '')}
                            onChange={(e) => setSelectedRow(prev => ({ ...prev, ['일수']: e.target.value }))}
                          />
                        </>
                      ) : (
                        <div className="col-span-3" />
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-6 gap-3 items-center">
                    <label className="text-[13px] font-semibold text-gray-600">가는편</label>
                    <input
                      className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                      value={String(selectedRow?.['출발편명'] ?? '')}
                      onChange={(e) => setSelectedRow(prev => ({ ...prev, ['출발편명']: e.target.value }))}
                    />
                    <label className="text-[13px] font-semibold text-gray-600">오는편</label>
                    <input
                      className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                      value={String(selectedRow?.['귀국편명'] ?? '')}
                      onChange={(e) => setSelectedRow(prev => ({ ...prev, ['귀국편명']: e.target.value }))}
                    />
                  </div>

                  <div className="grid grid-cols-6 gap-3 items-center">
                    <label className="text-[13px] font-semibold text-gray-600">출발지</label>
                    <div className="col-span-2 relative" ref={departEditRef}>
                      <button
                        type="button"
                        className="w-full border border-gray-300 rounded-lg h-9 px-2.5 text-sm text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        onClick={() => setDepartAirportOpen((v) => !v)}
                      >
                        {String(selectedRow?.['출발공항명'] ?? '') || String(selectedRow?.['출발공항'] ?? '') || '선택하세요'}
                      </button>
                      {departAirportOpen && (
                        <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow">
                          <div className="p-2 border-b">
                            <input
                              autoFocus
                              type="text"
                              className="w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
                              placeholder="검색 (코드/명)"
                              value={departAirportFilter}
                              onChange={(e) => setDepartAirportFilter(e.target.value)}
                            />
                          </div>
                          <div className="max-h-56 overflow-auto">
                            {filteredDepartAirports.length === 0 && (
                              <div className="px-3 py-2 text-sm text-gray-500">검색 결과가 없습니다</div>
                            )}
                            {filteredDepartAirports.map((r) => {
                              const code = String(r?.코드 ?? '');
                              const label = String(r?.코드명 ?? '');
                              return (
                                <button
                                  key={`${code}-${label}`}
                                  type="button"
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${String(selectedRow?.['출발공항'] ?? '') === code ? 'bg-blue-50' : ''}`}
                                  onClick={() => {
                                    setSelectedRow((prev) => ({ ...prev, ['출발공항']: code, ['출발공항명']: label }));
                                    setDepartAirportOpen(false);
                                  }}
                                >
                                  {label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    <label className="text-[13px] font-semibold text-gray-600">목적지</label>
                    <div className="col-span-2 relative" ref={arriveEditRef}>
                      <button
                        type="button"
                        className="w-full border border-gray-300 rounded-lg h-9 px-2.5 text-sm text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        onClick={() => setArriveAirportOpen((v) => !v)}
                      >
                        {String(selectedRow?.['목적공항명'] ?? '') || String(selectedRow?.['목적공항'] ?? '') || '선택하세요'}
                      </button>
                      {arriveAirportOpen && (
                        <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow">
                          <div className="p-2 border-b">
                            <input
                              autoFocus
                              type="text"
                              className="w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
                              placeholder="검색 (코드/명)"
                              value={arriveAirportFilter}
                              onChange={(e) => setArriveAirportFilter(e.target.value)}
                            />
                          </div>
                          <div className="max-h-56 overflow-auto">
                            {filteredArriveAirports.length === 0 && (
                              <div className="px-3 py-2 text-sm text-gray-500">검색 결과가 없습니다</div>
                            )}
                            {filteredArriveAirports.map((r) => {
                              const code = String(r?.코드 ?? '');
                              const label = String(r?.코드명 ?? '');
                              return (
                                <button
                                  key={`${code}-${label}`}
                                  type="button"
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${String(selectedRow?.['목적공항'] ?? '') === code ? 'bg-blue-50' : ''}`}
                                  onClick={() => {
                                    setSelectedRow((prev) => ({ ...prev, ['목적공항']: code, ['목적공항명']: label }));
                                    setArriveAirportOpen(false);
                                  }}
                                >
                                  {label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 items-center">
                    <div className="text-[13px] font-semibold text-gray-600">시작일 ~ 종료일</div>
                    <div className="py-3 col-span-2 text-sm text-gray-900">
                      {String(selectedRow?.['시리즈시작일'] ?? '')} ~ {String(selectedRow?.['시리즈종료일'] ?? '')}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-3 items-center">
                    <label className="text-[13px] font-semibold text-gray-600">호텔명</label>
                    <input
                      className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                      value={String(selectedRow?.['호텔명'] ?? '')}
                      onChange={(e) => setSelectedRow(prev => ({ ...prev, ['호텔명']: e.target.value }))}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3 items-center">
                    <label className="text-[13px] font-semibold text-gray-600">객실타입</label>
                    <input
                      className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                      value={String(selectedRow?.['객실타입'] ?? '')}
                      onChange={(e) => setSelectedRow(prev => ({ ...prev, ['객실타입']: e.target.value }))}
                    />
                  </div>
                </>
              )}

              <div className="grid grid-cols-3 gap-3 items-center">
                <label className="text-[13px] font-semibold text-gray-600">좌석 확인</label>
                <div className="col-span-2">
                  <button
                    type="button"
                    className="w-full h-9 rounded-lg px-3.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700"
                    onClick={async () => {
                      const seriesNo = Number(selectedRow?.['시리즈번호'] ?? 0);
                      if (!seriesNo) {
                        alert('시리즈번호가 없습니다.');
                        return;
                      }
                      try {
                        setLoadingSeatData(true);
                        const today = new Date();
                        const oneYearLater = new Date(today);
                        oneYearLater.setFullYear(today.getFullYear() + 1);
                        const startDate = today.toISOString().split('T')[0];
                        const endDate = oneYearLater.toISOString().split('T')[0];
                        const data = await getSeriesManagement({
                          startDate,
                          endDate,
                          searchCriteria: '출발일',
                          seriesNo,
                        });
                        setSeatData(Array.isArray(data) ? data : []);
                        setIsSeatModalOpen(true);
                      } catch (e) {
                        alert('좌석 정보를 불러오지 못했습니다.');
                        setSeatData([]);
                      } finally {
                        setLoadingSeatData(false);
                      }
                    }}
                    disabled={loadingSeatData}
                  >
                    {loadingSeatData ? '불러오는 중...' : '공유재고 등록 확인'}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 items-center">
                <label className="text-[13px] font-semibold text-gray-600">공급거래처</label>
                <div className="col-span-2">
                  <div className="relative" ref={supplierEditRef}>
                    <button
                      type="button"
                      className="w-full border border-gray-300 rounded-lg h-9 px-2.5 text-sm text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                      onClick={() => setIsSupplierOpen((v) => !v)}
                    >
                      {String(selectedRow?.['공급거래처'] ?? '') || '선택하세요'}
                    </button>
                    {isSupplierOpen && (
                      <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow">
                        <div className="p-2 border-b">
                          <input
                            autoFocus
                            type="text"
                            className="w-full h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
                            placeholder="검색 (코드/이름)"
                            value={supplierFilter}
                            onChange={(e) => setSupplierFilter(e.target.value)}
                          />
                        </div>
                        <div className="max-h-56 overflow-auto">
                          {filteredSuppliers.length === 0 && (
                            <div className="px-3 py-2 text-sm text-gray-500">검색 결과가 없습니다</div>
                          )}
                          {filteredSuppliers.map((r) => {
                            const v = String(r?.코드 ?? '');
                            const label = String(r?.코드명 ?? '');
                            return (
                              <button
                                key={v}
                                type="button"
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${String(selectedRow?.['공급거래처번호'] ?? '') === v ? 'bg-blue-50' : ''}`}
                                onClick={() => {
                                  setSelectedRow((prev) => ({
                                    ...prev,
                                    ['공급거래처번호']: v,
                                    ['공급거래처']: label,
                                  }));
                                  setIsSupplierOpen(false);
                                }}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                        <div className="p-2 border-t bg-gray-50">
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={newSupplierName}
                              onChange={(e) => setNewSupplierName(e.target.value)}
                              className="flex-1 h-9 rounded-lg border border-gray-300 bg-white px-2.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
                              placeholder="새 거래처명 입력"
                            />
                            <button
                              type="button"
                              onClick={handleCreateSupplier}
                              disabled={creatingSupplier || !newSupplierName.trim()}
                              className="h-9 rounded-lg px-3.5 text-sm font-semibold bg-blue-600 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700"
                            >
                              {creatingSupplier ? '추가 중...' : '신규 추가'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="pt-2 flex justify-between items-center">
                <button
                  className="h-9 rounded-lg px-3.5 text-sm font-semibold bg-red-50 text-red-600 hover:bg-red-100"
                  onClick={async () => {
                    const no = Number(selectedRow?.['시리즈번호'] ?? 0);
                    if (!no) return;
                    const ok = window.confirm('해당 시리즈를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.');
                    if (!ok) return;
                    try {
                      const resp = await deleteSharedSeries({ sharedSeriesNo: no });
                      // Try to surface API-provided message (e.g., 삭제 불가 사유)
                      let apiMsg = '';
                      try {
                        const first = resp?.table?.[0] || resp?.table1?.[0] || resp?.Table?.[0] || resp?.Table1?.[0] || resp?.result?.[0] || null;
                        if (first && typeof first === 'object') {
                          apiMsg = first?.메시지 || first?.message || first?.MESSAGE || first?.오류 || first?.error || '';
                        }
                        if (!apiMsg && resp && typeof resp === 'object') {
                          for (const k of Object.keys(resp)) {
                            const v = resp[k];
                            if (typeof v === 'string' && (k.includes('메시') || k.toLowerCase().includes('message'))) {
                              apiMsg = v;
                              break;
                            }
                          }
                        }
                      } catch (_) {}

                      if (apiMsg) {
                        alert(apiMsg);
                        // If API explicitly indicates success, proceed to close and refresh
                        if (apiMsg.includes('성공') || apiMsg.toLowerCase().includes('success')) {
                          setIsEditorOpen(false);
                          fetchList();
                        }
                        return;
                      }

                      // Fallback success UI when no message is provided
                      alert('삭제되었습니다.');
                      setIsEditorOpen(false);
                      fetchList();
                    } catch (e) {
                      const msg = (e && (e.apiMessage || e.message)) || '';
                      if (msg) {
                        alert(msg);
                      } else {
                        alert('삭제에 실패했습니다.');
                      }
                    }
                  }}
                >
                  삭제
                </button>
                <div className="flex items-center gap-2">
                  <button
                    className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white hover:bg-gray-50"
                    onClick={() => setIsEditorOpen(false)}
                  >
                    닫기
                  </button>
                  <button
                    className="h-9 rounded-lg px-3.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700"
                    onClick={async () => {
                    try {
                      const params = {
                        sharedSeriesNo: Number(selectedRow?.['시리즈번호'] ?? 0),
                        supplierPartnerNo: selectedRow?.['공급거래처번호'] != null ? Number(selectedRow['공급거래처번호']) : null,
                        seriesName: String(selectedRow?.['시리즈명'] ?? ''),
                        stockType: String(seriesType),
                        contractType: String(normalizeContractCode(selectedRow?.['계약종류'] ?? '')),
                        departAirportCode: String(selectedRow?.['출발공항'] ?? ''),
                        arriveAirportCode: String(selectedRow?.['목적공항'] ?? ''),
                        penaltyRule: '',
                        color: String(selectedRow?.['공유색상'] ?? selectedRow?.['색상'] ?? ''),
                        departFlightNo: String(selectedRow?.['출발편명'] ?? ''),
                        returnFlightNo: String(selectedRow?.['귀국편명'] ?? ''),
                        nights: selectedRow?.['박수'] != null ? Number(selectedRow['박수']) : null,
                        days: selectedRow?.['일수'] != null ? Number(selectedRow['일수']) : null,
                        oneWayType: String(selectedRow?.['편도'] ?? '왕복'),
                        airlineExtras: '',
                        hotelName: seriesType === '347002' ? String(selectedRow?.['호텔명'] ?? '') : null,
                        roomType: seriesType === '347002' ? String(selectedRow?.['객실타입'] ?? '') : null,
                      };
                      const resp = await saveSharedInventory(params);
                      alert('저장되었습니다.');
                      setIsEditorOpen(false);
                      fetchList();
                    } catch (e) {
                      alert('저장에 실패했습니다.');
                    }
                  }}
                >
                  저장
                </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isSeatModalOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setIsSeatModalOpen(false)} />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl bg-white shadow-2xl rounded-2xl z-50 flex flex-col max-h-[90vh]">
            <div className="px-6 pt-5 pb-3 flex items-center justify-between">
              <h2 className="text-[17px] font-bold text-gray-900">공유재고 좌석 현황</h2>
              <button
                className="h-8 w-8 rounded-lg text-gray-400 hover:bg-gray-100 text-xl"
                onClick={() => setIsSeatModalOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="p-6 overflow-auto flex-1">
              {seatData.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  좌석 정보가 없습니다.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="h-10 text-left px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap">출발일</th>
                        <th className="h-10 text-left px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap">요일</th>
                        <th className="h-10 text-left px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap">블럭수</th>
                        <th className="h-10 text-left px-3 text-[13px] font-medium text-gray-500 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap">판매인원</th>
                      </tr>
                    </thead>
                    <tbody>
                      {seatData.map((row, idx) => {
                        const departDate = String(row?.['출발일'] ?? '');
                        const weekday = String(row?.['요일'] ?? '');
                        const blockCount = String(row?.['블럭수'] ?? '');
                        const salesCount = String(row?.['판매인원'] ?? '');
                        return (
                          <tr
                            key={idx}
                            className="border-b border-gray-200 hover:bg-gray-50 cursor-pointer"
                            onClick={() => openSeatEdit(row)}
                          >
                            <td className="px-3 py-3 whitespace-nowrap text-gray-800">{departDate}</td>
                            <td className="px-3 py-3 whitespace-nowrap text-gray-800">{weekday}</td>
                            <td className="px-3 py-3 whitespace-nowrap text-gray-800">{blockCount}</td>
                            <td className="px-3 py-3 whitespace-nowrap text-gray-800">{salesCount}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="px-6 pb-5 pt-3 flex justify-between items-center">
              <button
                className="h-9 rounded-lg px-3.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700"
                onClick={() => {
                  setRegisterForm({
                    startDate: '',
                    endDate: '',
                    seatCount: '',
                    weekdays: [1, 2, 3, 4, 5, 6, 7],
                  });
                  setIsRegisterModalOpen(true);
                }}
              >
                좌석 등록
              </button>
              <button
                className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white hover:bg-gray-50"
                onClick={() => setIsSeatModalOpen(false)}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {isSeatEditModalOpen && (
        <div className="fixed inset-0 z-[70]">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setIsSeatEditModalOpen(false); setSeatEditDetail(null); }} />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white shadow-2xl rounded-2xl z-60 flex flex-col">
            <div className="px-6 pt-5 pb-3 flex items-center justify-between">
              <h2 className="text-[17px] font-bold text-gray-900">좌석수 편집</h2>
              <button
                className="h-8 w-8 rounded-lg text-gray-400 hover:bg-gray-100 text-xl"
                onClick={() => { setIsSeatEditModalOpen(false); setSeatEditDetail(null); }}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              {isSeatDetailLoading || !seatEditDetail ? (
                <div className="text-center text-gray-500">불러오는 중...</div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-3 items-center">
                    <div className="text-[13px] font-semibold text-gray-600">출발일</div>
                    <div className="col-span-2 text-sm text-gray-900">
                      {String(seatEditDetail?.['출발일'] ?? seatEditDetail?.['네임티엘'] ?? '')}
                    </div>
                  </div>
                 
                  <div className="grid grid-cols-3 gap-3 items-center">
                    <label className="text-[13px] font-semibold text-gray-600">좌석수</label>
                    <input
                      type="number"
                      min="0"
                      className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                      value={seatEditSeatCount}
                      onChange={(e) => setSeatEditSeatCount(e.target.value)}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="px-6 pb-5 pt-3 flex justify-end items-center gap-2">
              <button
                className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white hover:bg-gray-50"
                onClick={() => { setIsSeatEditModalOpen(false); setSeatEditDetail(null); }}
              >
                닫기
              </button>
              <button
                className="h-9 rounded-lg px-3.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleSaveSeatEdit}
                disabled={isSeatSaving || isSeatDetailLoading}
              >
                {isSeatSaving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isRegisterModalOpen && (
        <div className="fixed inset-0 z-[60]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setIsRegisterModalOpen(false)} />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white shadow-2xl rounded-2xl z-50 flex flex-col">
            <div className="px-6 pt-5 pb-3 flex items-center justify-between">
              <h2 className="text-[17px] font-bold text-gray-900">좌석 등록</h2>
              <button
                className="h-8 w-8 rounded-lg text-gray-400 hover:bg-gray-100 text-xl"
                onClick={() => setIsRegisterModalOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-3 gap-3 items-center">
                <label className="text-[13px] font-semibold text-gray-600">시작일</label>
                <input
                  type="date"
                  className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                  value={registerForm.startDate}
                  onChange={(e) => setRegisterForm(prev => ({ ...prev, startDate: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-3 gap-3 items-center">
                <label className="text-[13px] font-semibold text-gray-600">종료일</label>
                <input
                  type="date"
                  className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                  value={registerForm.endDate}
                  onChange={(e) => setRegisterForm(prev => ({ ...prev, endDate: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-3 gap-3 items-center">
                <label className="text-[13px] font-semibold text-gray-600">좌석수</label>
                <input
                  type="number"
                  min="0"
                  className="col-span-2 border border-gray-300 rounded-lg h-9 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                  value={registerForm.seatCount}
                  onChange={(e) => setRegisterForm(prev => ({ ...prev, seatCount: e.target.value }))}
                  placeholder="좌석수 입력"
                />
              </div>
              <div className="grid grid-cols-3 gap-3 items-start">
                <label className="text-[13px] font-semibold text-gray-600 pt-2">적용요일</label>
                <div className="col-span-2 space-y-2">
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 1, label: '월' },
                      { value: 2, label: '화' },
                      { value: 3, label: '수' },
                      { value: 4, label: '목' },
                      { value: 5, label: '금' },
                      { value: 6, label: '토' },
                      { value: 7, label: '일' },
                    ].map(({ value, label }) => (
                      <label key={value} className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={registerForm.weekdays.includes(value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setRegisterForm(prev => ({
                                ...prev,
                                weekdays: [...prev.weekdays, value].sort((a, b) => a - b),
                              }));
                            } else {
                              setRegisterForm(prev => ({
                                ...prev,
                                weekdays: prev.weekdays.filter(w => w !== value),
                              }));
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-700">{label}</span>
                      </label>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="text-xs text-blue-600 hover:text-blue-700"
                    onClick={() => {
                      const allSelected = registerForm.weekdays.length === 7;
                      setRegisterForm(prev => ({
                        ...prev,
                        weekdays: allSelected ? [] : [1, 2, 3, 4, 5, 6, 7],
                      }));
                    }}
                  >
                    {registerForm.weekdays.length === 7 ? '전체 해제' : '전체 선택'}
                  </button>
                </div>
              </div>
            </div>
            <div className="px-6 pb-5 pt-3 flex justify-end gap-2">
              <button
                className="h-9 rounded-lg px-3.5 text-sm font-semibold border border-gray-300 bg-white hover:bg-gray-50"
                onClick={() => setIsRegisterModalOpen(false)}
                disabled={isRegistering}
              >
                취소
              </button>
              <button
                className="h-9 rounded-lg px-3.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={async () => {
                  const seriesNo = Number(selectedRow?.['시리즈번호'] ?? 0);
                  if (!seriesNo) {
                    alert('시리즈번호가 없습니다.');
                    return;
                  }
                  if (!registerForm.startDate || !registerForm.endDate) {
                    alert('시작일과 종료일을 입력해주세요.');
                    return;
                  }
                  if (!registerForm.seatCount || Number(registerForm.seatCount) <= 0) {
                    alert('좌석수를 입력해주세요.');
                    return;
                  }
                  if (registerForm.weekdays.length === 0) {
                    alert('적용요일을 선택해주세요.');
                    return;
                  }
                  try {
                    setIsRegistering(true);
                    const weekdaysStr = registerForm.weekdays.sort((a, b) => a - b).join(',');
                    await registerSeriesInventory({
                      sharedSeriesNo: seriesNo,
                      weekdays: weekdaysStr,
                      startDate: registerForm.startDate,
                      endDate: registerForm.endDate,
                      seatCount: registerForm.seatCount,
                    });
                    alert('좌석이 등록되었습니다.');
                    setIsRegisterModalOpen(false);
                    // 좌석 현황 다시 로드
                    const today = new Date();
                    const oneYearLater = new Date(today);
                    oneYearLater.setFullYear(today.getFullYear() + 1);
                    const startDate = today.toISOString().split('T')[0];
                    const endDate = oneYearLater.toISOString().split('T')[0];
                    const data = await getSeriesManagement({
                      startDate,
                      endDate,
                      searchCriteria: '출발일',
                      seriesNo,
                    });
                    setSeatData(Array.isArray(data) ? data : []);
                  } catch (e) {
                    alert('좌석 등록에 실패했습니다.');
                  } finally {
                    setIsRegistering(false);
                  }
                }}
                disabled={isRegistering}
              >
                {isRegistering ? '등록 중...' : '등록'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SharedInventoryManagement;
